import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Logo } from '../components/ui/Logo'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Avatar, AvatarFallback } from '../components/ui/avatar'
import { useAuth } from '../hooks/use-auth'
import { get, post } from '../helper/network'
import API from '../helper/api'
import { ArrowLeft, Eye, EyeOff, Building2 } from 'lucide-react'
import { useI18n } from '../i18n'

interface BrandingConfig {
  enterpriseName: string
  shortCode: string
  logoUrl: string
  themePrimaryColor: string
  themeSecondaryColor: string
  welcomeText: string
  loginBgUrl: string
}

export default function LoginPage() {
  const navigate = useNavigate()
  const { loginDirect, loginByPhone } = useAuth()
  const { t } = useI18n()
  const [searchParams] = useSearchParams()
  const shortCode = searchParams.get('shortCode')

  const [loginMethod, setLoginMethod] = useState<'password' | 'code'>('password')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // 企业品牌
  const [branding, setBranding] = useState<BrandingConfig | null>(null)
  const primaryColor = branding?.themePrimaryColor || '#f97316'

  // 注册相关
  const [showRegister, setShowRegister] = useState(false)
  const [registerSuccess, setRegisterSuccess] = useState('')

  // 验证码倒计时
  const [codeCountdown, setCodeCountdown] = useState(0)
  useEffect(() => {
    if (codeCountdown <= 0) return
    const timer = setTimeout(() => setCodeCountdown(codeCountdown - 1), 1000)
    return () => clearTimeout(timer)
  }, [codeCountdown])

  // 加载企业品牌
  useEffect(() => {
    if (shortCode) {
      get(API.GET_ENTERPRISE_BRANDING, { shortCode }).then((res: any) => {
        if (res && res.code === 200 && res.data) {
          setBranding(res.data)
        }
      }).catch(() => {})
    }
  }, [shortCode])

  const displayName = branding?.enterpriseName || 'PrimeBook'
  const welcomeText = branding?.welcomeText || (branding ? `欢迎回来` : '欢迎回来')

  const sendCode = async (phone: string) => {
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError(t('请输入正确的手机号', 'Enter a valid phone number'))
      return
    }
    try {
      const res: any = await post(API.POST_SEND_CODE, { phone })
      if (res && res.code === 200) {
        setCodeCountdown(60)
      } else {
        setError(res?.message || t('发送验证码失败', 'Failed to send code'))
      }
    } catch {
      setError(t('网络错误，请稍后重试', 'Network error'))
    }
  }

  const handlePasswordLogin = async (phone: string, password: string) => {
    const res: any = await post(API.POST_LOGIN, { phone, password })
    if (res && res.code === 200) {
      loginDirect(phone, res.data)
      navigate('/workspace')
    } else {
      setError(res?.message || t('登录失败，请检查手机号和密码', 'Login failed'))
    }
  }

  const handleCodeLogin = async (phone: string, code: string) => {
    const res: any = await post(API.POST_LOGIN_PHONE, { phone, templateCode: code })
    if (res && res.code === 200) {
      loginByPhone(phone, res.data)
      navigate('/workspace')
    } else {
      setError(res?.message || t('登录失败，验证码错误或已过期', 'Login failed, invalid code'))
    }
  }

  const handleLoginSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const formData = new FormData(e.currentTarget)
    const phone = formData.get('phone') as string

    try {
      if (loginMethod === 'password') {
        const password = formData.get('password') as string
        await handlePasswordLogin(phone, password)
      } else {
        const code = formData.get('code') as string
        await handleCodeLogin(phone, code)
      }
    } catch {
      setError(t('网络错误，请稍后重试', 'Network error'))
    }

    setLoading(false)
  }

  const handleRegisterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')
    setRegisterSuccess('')
    setLoading(true)

    const formData = new FormData(e.currentTarget)
    const phone = formData.get('regPhone') as string
    const nickName = formData.get('nickName') as string
    const password = formData.get('regPassword') as string
    const verificationCode = formData.get('regCode') as string

    try {
      const res: any = await post(API.POST_REGISTER_PHONE, { phone, nickName, verificationCode, password })
      if (res && res.code === 200) {
        setRegisterSuccess(t('注册成功！请登录', 'Registered! Please login'))
        setShowRegister(false)
        setLoginMethod('password')
      } else {
        setError(res?.message || t('注册失败', 'Registration failed'))
      }
    } catch {
      setError(t('网络错误，请稍后重试', 'Network error'))
    }

    setLoading(false)
  }

  const [regCodeCountdown, setRegCodeCountdown] = useState(0)
  useEffect(() => {
    if (regCodeCountdown <= 0) return
    const timer = setTimeout(() => setRegCodeCountdown(regCodeCountdown - 1), 1000)
    return () => clearTimeout(timer)
  }, [regCodeCountdown])

  const handleRegSendCode = async (phone: string) => {
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError(t('请输入正确的手机号', 'Enter a valid phone number'))
      return
    }
    try {
      const res: any = await post(API.POST_SEND_CODE, { phone })
      if (res && res.code === 200) {
        setRegCodeCountdown(60)
      } else {
        setError(res?.message || t('发送验证码失败', 'Failed to send code'))
      }
    } catch {
      setError(t('网络错误，请稍后重试', 'Network error'))
    }
  }

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0">
        {branding?.loginBgUrl ? (
          <img
            src={branding.loginBgUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <>
            <div
              className="absolute -top-[30%] -left-[10%] w-[60%] h-[60%] rounded-full opacity-50"
              style={{ background: `radial-gradient(circle, ${primaryColor}25, transparent 70%)` }}
            />
            <div
              className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] rounded-full opacity-40"
              style={{ background: `radial-gradient(circle, ${primaryColor}18, transparent 70%)` }}
            />
          </>
        )}
      </div>

      <div className="w-full max-w-sm relative z-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('返回首页', 'Back')}
        </Link>

        <div className="rounded-2xl bg-white/70 backdrop-blur-xl border border-white/80 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
          {showRegister ? (
            <>
              <div className="text-center mb-7">
                <Link to="/" className="inline-flex items-center gap-2 text-xl font-semibold mb-3">
                  {branding?.logoUrl ? (
                    <img src={branding.logoUrl} alt="" className="h-7 w-7 rounded-lg object-cover" />
                  ) : (
                    <>
                      <Logo size={28} />
                      <span className="text-[#1a1a1a]">{displayName}</span>
                      <span style={{ color: primaryColor }}>AI</span>
                    </>
                  )}
                </Link>
                <h1 className="text-lg font-semibold text-[#1a1a1a]">{t('创建账号', 'Create Account')}</h1>
                <p className="text-xs text-gray-400 mt-1">
                  {branding
                    ? t(`注册以加入 ${branding.enterpriseName}`, `Join ${branding.enterpriseName}`)
                    : t('注册以开始使用 PrimeBook AI', 'Sign up for PrimeBook AI')
                  }
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 text-red-500 text-xs">{error}</div>
              )}

              <form onSubmit={handleRegisterSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="regPhone" className="text-xs text-gray-500">{t('手机号', 'Phone')}</Label>
                  <Input
                    id="regPhone" name="regPhone" type="tel"
                    placeholder={t('请输入手机号', 'Enter phone number')}
                    required pattern="^1[3-9]\d{9}$"
                    title={t('请输入正确的11位手机号', 'Enter valid 11-digit phone')}
                    className="bg-white/60 border-black/[0.06] text-[#1a1a1a] placeholder:text-gray-300 focus:border-orange-200 rounded-xl h-10 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="nickName" className="text-xs text-gray-500">{t('姓名', 'Name')}</Label>
                  <Input
                    id="nickName" name="nickName" type="text"
                    placeholder={t('请输入您的姓名', 'Enter your name')}
                    required maxLength={50}
                    className="bg-white/60 border-black/[0.06] text-[#1a1a1a] placeholder:text-gray-300 focus:border-orange-200 rounded-xl h-10 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="regCode" className="text-xs text-gray-500">{t('验证码', 'Code')}</Label>
                  <div className="flex gap-2">
                    <Input
                      id="regCode" name="regCode" type="text"
                      placeholder={t('请输入验证码', 'Enter code')}
                      required maxLength={6} pattern="\d{6}"
                      className="bg-white/60 border-black/[0.06] text-[#1a1a1a] placeholder:text-gray-300 focus:border-orange-200 rounded-xl h-10 text-sm flex-1"
                    />
                    <Button
                      type="button" variant="outline"
                      className="shrink-0 h-10 px-3 text-xs rounded-xl border-black/[0.06]"
                      disabled={regCodeCountdown > 0}
                      onClick={() => {
                        const phone = (document.getElementById('regPhone') as HTMLInputElement)?.value
                        if (phone) handleRegSendCode(phone)
                      }}
                    >
                      {regCodeCountdown > 0 ? `${regCodeCountdown}s` : t('获取验证码', 'Get Code')}
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="regPassword" className="text-xs text-gray-500">{t('密码', 'Password')}</Label>
                  <div className="relative">
                    <Input
                      id="regPassword" name="regPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('设置密码（6位以上）', 'Set password (6+ chars)')}
                      required minLength={6} maxLength={100}
                      className="bg-white/60 border-black/[0.06] text-[#1a1a1a] placeholder:text-gray-300 focus:border-orange-200 rounded-xl h-10 text-sm pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full text-white h-10 text-sm font-medium rounded-xl"
                  style={{ backgroundColor: primaryColor }}
                  disabled={loading}
                >
                  {loading ? t('处理中...', 'Loading...') : t('注册', 'Sign Up')}
                </Button>
              </form>

              <div className="mt-5 text-center">
                <p className="text-xs text-gray-400">
                  {t('已有账号？', 'Already have an account?')}
                  <button
                    type="button"
                    className="font-medium ml-1 transition-colors"
                    style={{ color: primaryColor }}
                    onClick={() => { setShowRegister(false); setError(''); setRegisterSuccess('') }}
                  >
                    {t('立即登录', 'Sign In')}
                  </button>
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-7">
                <Link to="/" className="inline-flex items-center gap-2 text-xl font-semibold mb-3">
                  {branding?.logoUrl ? (
                    <img src={branding.logoUrl} alt="" className="h-7 w-7 rounded-lg object-cover" />
                  ) : (
                    <>
                      <Logo size={28} />
                      <span className="text-[#1a1a1a]">{displayName}</span>
                      <span style={{ color: primaryColor }}>AI</span>
                    </>
                  )}
                </Link>
                <h1 className="text-lg font-semibold text-[#1a1a1a]">{t(welcomeText, welcomeText)}</h1>
                <p className="text-xs text-gray-400 mt-1">
                  {branding
                    ? t(`登录 ${branding.enterpriseName} 账号`, `Sign in to ${branding.enterpriseName}`)
                    : t('登录您的 PrimeBook AI 账号', 'Sign in to PrimeBook AI')
                  }
                </p>
                {branding && (
                  <div className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-lg" style={{ backgroundColor: `${primaryColor}10` }}>
                    <Building2 className="h-3 w-3" style={{ color: primaryColor }} />
                    <span className="text-[11px] font-medium" style={{ color: primaryColor }}>{branding.enterpriseName}</span>
                  </div>
                )}
              </div>

              {registerSuccess && (
                <div className="mb-4 p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-600 text-xs">
                  {registerSuccess}
                </div>
              )}

              {error && (
                <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 text-red-500 text-xs">{error}</div>
              )}

              <form onSubmit={handleLoginSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="phone" className="text-xs text-gray-500">{t('手机号', 'Phone')}</Label>
                  <Input
                    id="phone" name="phone" type="tel"
                    placeholder={t('请输入手机号', 'Enter phone number')}
                    required pattern="^1[3-9]\d{9}$"
                    title={t('请输入正确的11位手机号', 'Enter valid 11-digit phone')}
                    className="bg-white/60 border-black/[0.06] text-[#1a1a1a] placeholder:text-gray-300 focus:border-orange-200 rounded-xl h-10 text-sm"
                  />
                </div>

                {loginMethod === 'password' ? (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="password" className="text-xs text-gray-500">{t('密码', 'Password')}</Label>
                      <div className="relative">
                        <Input
                          id="password" name="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder={t('请输入密码', 'Enter password')}
                          required
                          className="bg-white/60 border-black/[0.06] text-[#1a1a1a] placeholder:text-gray-300 focus:border-orange-200 rounded-xl h-10 text-sm pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      <button
                        type="button"
                        className="text-xs text-gray-400 hover:text-orange-500 transition-colors"
                        onClick={() => { setLoginMethod('code'); setError('') }}
                      >
                        {t('忘记密码？使用验证码登录', 'Forgot password? Use verification code')}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="code" className="text-xs text-gray-500">{t('验证码', 'Code')}</Label>
                      <div className="flex gap-2">
                        <Input
                          id="code" name="code" type="text"
                          placeholder={t('请输入验证码', 'Enter code')}
                          required maxLength={6} pattern="\d{6}"
                          className="bg-white/60 border-black/[0.06] text-[#1a1a1a] placeholder:text-gray-300 focus:border-orange-200 rounded-xl h-10 text-sm flex-1"
                        />
                        <Button
                          type="button" variant="outline"
                          className="shrink-0 h-10 px-3 text-xs rounded-xl border-black/[0.06]"
                          disabled={codeCountdown > 0}
                          onClick={() => {
                            const phone = (document.getElementById('phone') as HTMLInputElement)?.value
                            if (phone) sendCode(phone)
                          }}
                        >
                          {codeCountdown > 0 ? `${codeCountdown}s` : t('获取验证码', 'Get Code')}
                        </Button>
                      </div>
                    </div>
                    <div className="text-right">
                      <button
                        type="button"
                        className="text-xs text-gray-400 hover:text-orange-500 transition-colors"
                        onClick={() => { setLoginMethod('password'); setError('') }}
                      >
                        {t('使用密码登录', 'Sign in with password')}
                      </button>
                    </div>
                  </>
                )}

                <Button
                  type="submit"
                  className="w-full text-white h-10 text-sm font-medium rounded-xl"
                  style={{ backgroundColor: primaryColor }}
                  disabled={loading}
                >
                  {loading ? t('处理中...', 'Loading...') : t('登录', 'Sign In')}
                </Button>
              </form>

              <div className="mt-5 text-center">
                <p className="text-xs text-gray-400">
                  {t('还没有账号？', "Don't have an account?")}
                  <button
                    type="button"
                    className="font-medium ml-1 transition-colors"
                    style={{ color: primaryColor }}
                    onClick={() => { setShowRegister(true); setError(''); setRegisterSuccess('') }}
                  >
                    {t('立即注册', 'Sign Up')}
                  </button>
                </p>
              </div>

              <p className="mt-2 text-center text-[11px] text-gray-300">
                {t('注册即送10万Token免费额度', 'Get 100K free tokens on signup')}
              </p>
            </>
          )}
        </div>

        <p className="mt-5 text-center text-[11px] text-gray-300">
          {t('登录即表示您同意我们的服务条款和隐私政策', 'By signing in you agree to our Terms and Privacy Policy')}
        </p>
      </div>
    </div>
  )
}
